import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, UserPlus, FileBarChart2, Trash2, Copy, Download } from "lucide-react";

/* ------------------- Municipalities ------------------- */
function Municipalities({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", state: "", contact_email: "", active: true });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/municipalities").then((r) => { setItems(r.data); onChange?.(r.data); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/municipalities", { ...form, contact_email: form.contact_email || null });
      toast.success("Município criado");
      setOpen(false); setForm({ name: "", slug: "", state: "", contact_email: "", active: true }); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (m) => {
    await api.patch(`/admin/municipalities/${m.id}`, { active: !m.active });
    load();
  };

  const remove = async (m) => {
    if (!confirm(`Remover ${m.name}? Só é permitido se não houver pacientes.`)) return;
    try { await api.delete(`/admin/municipalities/${m.id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-navy-900 flex items-center gap-2"><Building2 className="w-5 h-5 text-teal-600" />Municípios credenciados</h2>
          <p className="text-sm text-slate-600 mt-1">Gerencie municípios com portal SUS ativo.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-teal-500 hover:bg-teal-600" data-testid="muni-new-btn"><Building2 className="w-4 h-4 mr-1" />Novo município</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Cadastrar município</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="muni-name" /></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e)=>setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")})} placeholder="ex: sao-paulo" data-testid="muni-slug" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>UF</Label><Input value={form.state} onChange={(e)=>setForm({...form, state: e.target.value.toUpperCase().slice(0,2)})} data-testid="muni-state" /></div>
                <div><Label>Email de contato</Label><Input value={form.contact_email} onChange={(e)=>setForm({...form, contact_email: e.target.value})} data-testid="muni-email" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy || !form.name || !form.slug} className="bg-teal-500 hover:bg-teal-600" data-testid="muni-create">Criar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3" data-testid="muni-list">
        {items.map((m) => {
          const url = `${window.location.origin}/sus/${m.slug}`;
          return (
            <Card key={m.id} className="card-soft" data-testid={`muni-item-${m.id}`}>
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-navy-900">{m.name}{m.state ? ` — ${m.state}` : ""}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.active ? "bg-teal-100 text-teal-800" : "bg-slate-200 text-slate-600"}`}>{m.active ? "Ativo" : "Inativo"}</span>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(url); toast.success("URL copiada"); }} className="text-xs text-slate-500 hover:text-teal-700 flex items-center gap-1 mt-1"><Copy className="w-3 h-3" />{url}</button>
                  <div className="text-xs text-slate-500 mt-1">{m.patients_count || 0} paciente(s)</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2"><span className="text-xs text-slate-600">Ativo</span><Switch checked={m.active} onCheckedChange={() => toggle(m)} data-testid={`muni-toggle-${m.id}`} /></div>
                  <Button size="sm" variant="ghost" onClick={()=>remove(m)} data-testid={`muni-delete-${m.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <p className="text-slate-500 text-sm">Nenhum município cadastrado.</p>}
      </div>
    </div>
  );
}

/* ------------------- SUS Patients ------------------- */
function Patients({ municipalities }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", cpf: "", municipality_id: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    const url = filter && filter !== "all" ? `/admin/patients?municipality_id=${filter}` : "/admin/patients";
    api.get(url).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;  // empty → backend auto-generates from CPF
      await api.post("/admin/patients", payload);
      toast.success("Paciente cadastrado");
      setOpen(false); setForm({ name: "", cpf: "", municipality_id: "", email: "", password: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!confirm(`Remover paciente ${p.name}?`)) return;
    await api.delete(`/admin/patients/${p.id}`);
    toast.success("Removido"); load();
  };

  const formatCpf = (raw) => {
    const d = (raw || "").replace(/\D/g, "").slice(0, 11);
    return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-navy-900 flex items-center gap-2"><UserPlus className="w-5 h-5 text-teal-600" />Pacientes SUS</h2>
          <p className="text-sm text-slate-600 mt-1">Cadastro manual — sem etapa de pagamento no agendamento.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="patient-filter"><SelectValue placeholder="Filtrar por município" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os municípios</SelectItem>
              {municipalities.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-500 hover:bg-teal-600" data-testid="patient-new-btn"><UserPlus className="w-4 h-4 mr-1" />Cadastrar</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Cadastrar paciente SUS</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome completo</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="patient-name" /></div>
                <div><Label>CPF</Label><Input value={form.cpf} onChange={(e)=>setForm({...form, cpf: e.target.value.replace(/\D/g, "").slice(0, 11)})} placeholder="somente números" data-testid="patient-cpf" /></div>
                <div><Label>Município</Label>
                  <Select value={form.municipality_id} onValueChange={(v)=>setForm({...form, municipality_id: v})}>
                    <SelectTrigger data-testid="patient-muni"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{municipalities.filter((m)=>m.active).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Email (opcional)</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} placeholder="Se vazio, gerado a partir do CPF" data-testid="patient-email" /></div>
                <div><Label>Senha inicial</Label><Input type="text" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} minLength={6} data-testid="patient-password" /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={busy || !form.name || form.cpf.length !== 11 || !form.municipality_id || form.password.length < 6} className="bg-teal-500 hover:bg-teal-600" data-testid="patient-create">Cadastrar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="card-soft">
        <CardContent className="p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="patient-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr><th className="p-3 text-left">Nome</th><th className="p-3 text-left">CPF</th><th className="p-3 text-left">Município</th><th className="p-3 text-left">Email</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`patient-row-${p.id}`}>
                  <td className="p-3 text-navy-900 font-medium">{p.name}</td>
                  <td className="p-3">{formatCpf(p.cpf)}</td>
                  <td className="p-3">{p.municipality_name || "—"}</td>
                  <td className="p-3 text-slate-500">{p.email}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={()=>remove(p)} data-testid={`patient-remove-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="p-6 text-slate-500 text-center">Nenhum paciente.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------- Reports ------------------- */
function Reports({ municipalities }) {
  const [muni, setMuni] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [report, setReport] = useState(null);

  const run = async () => {
    if (!muni) { toast.error("Selecione um município"); return; }
    const q = new URLSearchParams(); if (start) q.set("start", start); if (end) q.set("end", end);
    const { data } = await api.get(`/admin/reports/municipality/${muni}?${q.toString()}`);
    setReport(data);
  };

  const download = async (format) => {
    if (!muni) return;
    const q = new URLSearchParams({ format }); if (start) q.set("start", start); if (end) q.set("end", end);
    const token = localStorage.getItem("optia_token");
    const res = await fetch(`${BACKEND_URL}/api/admin/reports/municipality/${muni}/export?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Falha ao exportar"); return; }
    const blob = await res.blob();
    const muniObj = municipalities.find((m) => m.id === muni);
    const slug = muniObj?.slug || "municipio";
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `relatorio-${slug}-${today}.${format}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-semibold text-navy-900 flex items-center gap-2"><FileBarChart2 className="w-5 h-5 text-teal-600" />Relatórios por município</h2>
        <p className="text-sm text-slate-600 mt-1">Consultas realizadas, pacientes ativos e distribuição por especialidade.</p>
      </div>

      <Card className="card-soft">
        <CardContent className="p-5 grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label>Município</Label>
            <Select value={muni} onValueChange={setMuni}>
              <SelectTrigger data-testid="report-muni"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{municipalities.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Início</Label><Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} data-testid="report-start" /></div>
          <div><Label>Fim</Label><Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} data-testid="report-end" /></div>
          <div className="sm:col-span-4 flex gap-2">
            <Button onClick={run} className="bg-teal-500 hover:bg-teal-600" data-testid="report-run">Gerar relatório</Button>
            {report && (
              <>
                <Button variant="outline" onClick={()=>download("xlsx")} data-testid="report-xlsx"><Download className="w-4 h-4 mr-1" />Excel</Button>
                <Button variant="outline" onClick={()=>download("pdf")} data-testid="report-pdf"><Download className="w-4 h-4 mr-1" />PDF</Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {report && (
        <div className="space-y-5" data-testid="report-content">
          <div className="grid sm:grid-cols-4 gap-3">
            {[
              { label: "Consultas totais", val: report.totals.consultations_total },
              { label: "Realizadas", val: report.totals.consultations_completed },
              { label: "Agendadas", val: report.totals.consultations_scheduled },
              { label: "Pacientes ativos", val: report.totals.active_patients },
            ].map((k) => (
              <Card key={k.label} className="card-soft"><CardContent className="p-5">
                <div className="text-xs text-slate-500">{k.label}</div>
                <div className="font-display text-3xl text-navy-900 mt-1">{k.val}</div>
              </CardContent></Card>
            ))}
          </div>

          <Card className="card-soft">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-navy-900">Por especialidade</h3>
              <div className="mt-3 space-y-1.5">
                {report.by_specialty.map((r) => (
                  <div key={r.specialty} className="flex items-center gap-3">
                    <div className="w-40 text-sm">{r.specialty}</div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-2 bg-teal-500" style={{ width: `${Math.min(100, (r.count / Math.max(1, report.totals.consultations_total)) * 100)}%` }} /></div>
                    <div className="w-10 text-right text-sm text-navy-900 font-medium">{r.count}</div>
                  </div>
                ))}
                {report.by_specialty.length === 0 && <p className="text-slate-500 text-sm">Sem consultas no período.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ------------------- Admin Panel ------------------- */
export default function Admin() {
  const { user, loading } = useAuth();
  const [munis, setMunis] = useState([]);
  useEffect(() => { api.get("/admin/municipalities").then((r) => setMunis(r.data)).catch(() => {}); }, []);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Carregando…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="container-optia py-10 animate-fade-in">
      <h1 className="font-display text-3xl font-semibold text-navy-900">Painel administrativo</h1>
      <p className="text-slate-600 mt-1">Portal SUS — municípios, pacientes e relatórios.</p>

      <Tabs defaultValue="munis" className="mt-8">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="munis" data-testid="tab-munis">Municípios</TabsTrigger>
          <TabsTrigger value="patients" data-testid="tab-patients">Pacientes SUS</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Relatórios</TabsTrigger>
        </TabsList>
        <TabsContent value="munis" className="mt-6"><Municipalities onChange={setMunis} /></TabsContent>
        <TabsContent value="patients" className="mt-6"><Patients municipalities={munis} /></TabsContent>
        <TabsContent value="reports" className="mt-6"><Reports municipalities={munis} /></TabsContent>
      </Tabs>
    </div>
  );
}
